import {
  HttpClient,
  HttpClientRequest,
  HttpClientResponse,
} from "@effect/platform";
import { Schema } from "@effect/schema";
import {
  Address,
  Amount,
  chains,
  getProdChainKeyFromProtocol,
  makeUsd,
  ProdChainKey,
  ProdProtocolKeyService,
  TokenUid,
  USD_ZERO,
} from "@silo/types";
import { Effect, pipe, Array, Option } from "effect";
import { RawUserReward, UserMerklReward, UserMerklRewardsInfo } from "./types";
import { getUserRewardMeta } from "./get-user-reward-meta";
import { KvClient } from "../kv-client";
import { convertToUsd } from "@silo/utils";
import { sumAll } from "effect/BigInt";
import { isNotNullable } from "effect/Predicate";
import { setMerklHistoricalRewardTokenArray } from "../kv-client/merkl-user-rewards-info";
import { RewardToken } from "../types";

export type GetUserRewardsInfoSuccess =
  Effect.Effect.Success<GetUserRewardsInfo>;
export type GetUserRewardsInfo = ReturnType<typeof getUserRewardsInfo>;
export type GetUserRewardsInfoError = Effect.Effect.Error<GetUserRewardsInfo>;
export type GetUserRewardsInfoContext =
  Effect.Effect.Context<GetUserRewardsInfo>;

export function getUserRewardsInfo(user: Address) {
  return Effect.gen(function* () {
    const prodProtocolKey = yield* ProdProtocolKeyService;

    const cachedUserRewardsInfo = yield* KvClient.getMerklUserRewardsInfo(
      prodProtocolKey,
      user
    );

    if (isNotNullable(cachedUserRewardsInfo)) {
      return cachedUserRewardsInfo;
    }

    const userRewardsInfo = yield* sourceUserRewardsInfo(user);

    yield* KvClient.setMerklUserRewardsInfo(userRewardsInfo);

    return userRewardsInfo;
  });
}

export type SourceUserRewardsInfo = ReturnType<typeof sourceUserRewardsInfo>;
export type SourceUserRewardsInfoSuccess =
  Effect.Effect.Success<SourceUserRewardsInfo>;
export type SourceUserRewardsInfoError =
  Effect.Effect.Error<SourceUserRewardsInfo>;
export type SourceUserRewardsInfoContext =
  Effect.Effect.Context<SourceUserRewardsInfo>;

export function sourceUserRewardsInfo(user: Address) {
  return Effect.gen(function* () {
    const prodProtocolKey = yield* ProdProtocolKeyService;
    const chainKey = getProdChainKeyFromProtocol(prodProtocolKey);

    const rawUserRewards = yield* getRawUserRewards(chainKey, user);

    const [excludedUserRewardMetaArray, userRewardMetaArray] =
      yield* Effect.partition(
        rawUserRewards,
        (rawUserReward) => getUserRewardMeta(rawUserReward),
        { concurrency: "unbounded" }
      );

    const tokens: TokenUid[] = userRewardMetaArray.map(
      ({ chainKey, pricingTokenAddress }) => ({
        chainKey,
        tokenAddress: pricingTokenAddress,
      })
    );

    const tokenPriceMap = yield* KvClient.getTokenPriceMap(tokens);

    const userRewardArray: UserMerklReward[] = userRewardMetaArray.map(
      (userRewardMeta) => {
        const {
          chainKey,
          pricingTokenAddress,
          unclaimed,
          rewardTokenDecimals,
        } = userRewardMeta;

        const priceUsd = pipe(
          tokenPriceMap.get({ chainKey, tokenAddress: pricingTokenAddress }),
          Option.getOrElse(() => USD_ZERO)
        );

        const unclaimedUsd = convertToUsd(
          unclaimed,
          rewardTokenDecimals,
          priceUsd
        );

        return {
          ...userRewardMeta,
          unclaimedUsd,
        };
      }
    );

    const rewards = userRewardArray.filter(
      (reward) => reward.protocolKey === prodProtocolKey
    );

    const totalUsd = pipe(
      rewards,
      Array.map((reward) => reward.unclaimedUsd),
      sumAll,
      makeUsd
    );

    const rewardTokenArray: RewardToken[] = rewards.map(
      ({ chainKey, pricingTokenAddress, pricingTokenSymbol }) => ({
        chainKey,
        tokenAddress: pricingTokenAddress,
        symbol: pricingTokenSymbol,
      })
    );

    yield* setMerklHistoricalRewardTokenArray(rewardTokenArray);

    const userRewardsInfo: UserMerklRewardsInfo = {
      protocolKey: prodProtocolKey,
      user,
      totalUsd,
      rewards,
    };

    return userRewardsInfo;
  });
}

export type GetRawUserRewards = ReturnType<typeof getRawUserRewards>;
export type GetRawUserRewardsSuccess = Effect.Effect.Success<GetRawUserRewards>;
export type GetRawUserRewardsError = Effect.Effect.Error<GetRawUserRewards>;
export type GetRawUserRewardsContext = Effect.Effect.Context<GetRawUserRewards>;

export function getRawUserRewards(chainKey: ProdChainKey, user: Address) {
  return Effect.gen(function* () {
    const chainId = chains[chainKey].id;
    const endpoint = `https://api.merkl.xyz/v3/userRewards?chainId=${chainId}&user=${user}`;

    const userRewardsResponse = yield* pipe(
      HttpClientRequest.get(endpoint),
      HttpClient.fetch,
      Effect.andThen(HttpClientResponse.schemaBodyJson(UserRewardsResponse)),
      Effect.scoped
    );

    const rawUserRewards: RawUserReward[] = [];

    for (const [rewardTokenAddress, tokenMeta] of Object.entries(
      userRewardsResponse
    )) {
      const { decimals, symbol, reasons } = tokenMeta;

      for (const [reasonKey, reasonMeta] of Object.entries(reasons)) {
        const { accumulated, unclaimed } = reasonMeta;

        rawUserRewards.push({
          chainKey,
          rewardTokenAddress,
          rewardTokenSymbol: symbol,
          rewardTokenDecimals: decimals,
          reasonKey,
          accumulated,
          unclaimed,
        });
      }
    }

    return rawUserRewards;
  });
}

export type UserRewardsResponse = typeof UserRewardsResponse.Type;
export type UserRewardsResponseEncoded = typeof UserRewardsResponse.Encoded;

const UserRewardsResponse = Schema.Record({
  key: Schema.String,
  value: Schema.Struct({
    accumulated: Amount,
    decimals: Schema.Number,
    reasons: Schema.Record({
      key: Schema.String,
      value: Schema.Struct({
        accumulated: Amount,
        unclaimed: Amount,
      }),
    }),
    symbol: Schema.String,
    unclaimed: Amount,
  }),
}).annotations({ identifier: "UserRewardsResponse" });
